import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * Renders an inline breadcrumb trail and emits BreadcrumbList JSON-LD for SEO.
 * Pass `items` as [{label, to?}] — the last item is shown as the current page.
 */
export default function Breadcrumbs({ items = [] }) {
  React.useEffect(() => {
    if (items.length < 2) return;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const ld = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.label,
        ...(it.to ? { item: base + it.to } : {}),
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-breadcrumb-ld", "true");
    el.text = JSON.stringify(ld);
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [items]);

  return (
    <nav className="flex items-center flex-wrap gap-1 text-xs text-zinc-500 font-mono-tag" data-testid="breadcrumbs" aria-label="breadcrumb">
      {items.map((it, i) => {
        const isLast = i === items.length - 1;
        return (
          <React.Fragment key={`${it.label}-${i}`}>
            {it.to && !isLast ? (
              <Link to={it.to} className="hover:text-zinc-900 transition">{it.label}</Link>
            ) : (
              <span className={isLast ? "text-zinc-800" : ""}>{it.label}</span>
            )}
            {!isLast && <ChevronRight className="h-3 w-3 text-zinc-300" />}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
